import _ from 'underscore';
import Promise from 'bluebird';
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import odata from '@bingads-webui-component-legacy/http/channel/odata';
import { Scenario, Activity, ActivityContext } from '@bingads-webui-react/hoc-with-signal';

const PREFERENCE_NAME_GOOGLE = 'Import.GoogleImport.SignupWithSmartGoogleImport';
const PREFERENCE_NAME_FACEBOOK = 'Import.FacebookImport.SignupWithSmartFacebookImport';

const getImportScheduleAndHistoryLink = (serverContext, preferenceType) => {
  const url = `${serverContext.ServicesEndpoints.CampaignUIAPI}vnext/importschedule`;
  let queryParams = '';
  const {
    CurrentAccount, CurrentCustomer, CurrentUser,
  } = serverContext || {};
  if (CurrentAccount && CurrentCustomer && CurrentUser) {
    const importType = {
      [PREFERENCE_NAME_GOOGLE]: 'googleimport',
      [PREFERENCE_NAME_FACEBOOK]: 'facebookimport',
    }[preferenceType] || '';
    if (importType) {
      queryParams = `?aid=${CurrentAccount.Id}&cid=${CurrentCustomer.Id}&uid=${CurrentUser.Id}&importType=${importType}`;
    } else {
      queryParams = `?aid=${CurrentAccount.Id}&cid=${CurrentCustomer.Id}&uid=${CurrentUser.Id}`;
    }
  }
  return `${url}${queryParams}`;
};

const getNavigationLinkMsg = (serverContext, i18n, preferenceType) => {
  const url = getImportScheduleAndHistoryLink(serverContext, preferenceType);
  const linkMsg = i18n.getString(
    _TL_('You can review your {{link}} at any time.'),
    { link: `<a href="${url}">${i18n.getString(_TL_('Import schedule & history'))}</a>` }
  );
  return linkMsg;
};

const showGoogleImportNotificationMsg = (status, serverContext, i18n) => {
  const { inProgress, importedCampaignsCount } = status || {};
  const linkMsg = getNavigationLinkMsg(serverContext, i18n, PREFERENCE_NAME_GOOGLE);
  if (inProgress) {
    const msg = i18n.getString(_TL_('We’re currently importing your campaigns from Google Ads.'));
    notificationApi.showInfo(`${msg} ${linkMsg}`);
  } else if (importedCampaignsCount > 0) {
    const msg = i18n.getString(_TL_('Your import from Google Ads has successfully completed.'));
    notificationApi.showInfo(`${msg} ${linkMsg}`);
  } else {
    const msg = i18n.getString(_TL_('We were unable to import any campaigns from your Google Ads account.'));
    notificationApi.showWarning(`${msg} ${linkMsg}`);
  }
};

const showFacebookImportNotificationMsg = (status, serverContext, i18n) => {
  const { inProgress, importedCampaignsCount } = status || {};
  const linkMsg = getNavigationLinkMsg(serverContext, i18n, PREFERENCE_NAME_FACEBOOK);
  if (inProgress) {
    const msg = i18n.getString(_TL_('We\'re currently importing your campaigns from Meta Ads.'));
    notificationApi.showInfo(`${msg} ${linkMsg}`);
  } else if (importedCampaignsCount > 0) {
    const msg = i18n.getString(_TL_('Your import from Meta Ads has successfully completed.'));
    notificationApi.showInfo(`${msg} ${linkMsg}`);
  } else {
    const msg = i18n.getString(_TL_('We were unable to import any campaigns from your Meta Ads account.'));
    notificationApi.showWarning(`${msg} ${linkMsg}`);
  }
};

const getImportedCampaignsCount = (entityStatistics) => {
  const { Keys, Values } = entityStatistics || {};
  const campaignEntityIndex = Keys && Keys.indexOf('Campaign');
  if (campaignEntityIndex >= 0) {
    const { Total } = Values[campaignEntityIndex] || {};
    if (_.isNumber(Total)) {
      return Total;
    }
  }
  return 0;
};

const getImportTaskStatus = (taskItemExecutionId) => {
  const url = `/Customers()/Accounts()/TaskItemExecutions(${taskItemExecutionId})`;
  return new Promise((resolve, reject) => {
    odata.get(url).then((data) => {
      // eslint-disable-next-line object-curly-newline
      const { ImportStatus, Status, ErrorCode, EntityStatistics } = data || {};
      const importedCampaignsCount = getImportedCampaignsCount(EntityStatistics);
      if (ImportStatus === 'Complete') {
        resolve({ importedCampaignsCount, inProgress: false });
      } else if (ImportStatus === 'Failed' || Status === 'Failed' || ErrorCode || ImportStatus === 'Cancelled') {
        reject(new Error());
      } else { // ongoing
        resolve({ inProgress: true });
      }
    }).catch((err) => {
      reject(err);
    });
  });
};

export const ImportStatusCheckerInternal = ({
  i18n, serverContext, preferenceService,
}) => {
  const [isShown, setIsShown] = useState(false);
  const activity = useContext(ActivityContext);

  useEffect(() => {
    if (!isShown && preferenceService && _.isFunction(preferenceService.findByNameAtAccountLevel)) {
      setIsShown(true);
      _.forEach([
        { preferenceName: PREFERENCE_NAME_GOOGLE, showNotificationMsg: showGoogleImportNotificationMsg },
        { preferenceName: PREFERENCE_NAME_FACEBOOK, showNotificationMsg: showFacebookImportNotificationMsg },
      ], ({ preferenceName, showNotificationMsg }) => {
        const { Id: id, Data: data } = preferenceService.findByNameAtAccountLevel(preferenceName) || {};
        if (id) {
          const taskItemExecutionId = data && data.importStatus && data.importStatus.taskItemExecutionId;
          if (taskItemExecutionId) {
            // get import task execution status
            getImportTaskStatus(taskItemExecutionId).then((status) => {
              activity.trace(`got import task status, show notification. status: ${JSON.stringify(status)}`);
              showNotificationMsg(status, serverContext, i18n);
            }).catch((err) => {
              activity.trace(`failed to get import task status, error: ${JSON.stringify(err)}`);
              showNotificationMsg(null, serverContext, i18n);
            });
          } else {
            activity.trace(`no taskItemExecutionId in preference, id: ${id}, pref: ${JSON.stringify(data)}`);
          }
          // remove preference
          preferenceService.del(id);
        } else {
          activity.trace('cannot find import status from preference service.');
        }
      });
    }
  }, [activity, i18n, isShown, preferenceService, serverContext]);

  return null;
};

ImportStatusCheckerInternal.propTypes = {
  serverContext: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

export const ImportStatusChecker = props => (
  <Scenario name="SignupWithImportStatusChecker">
    <Activity name="CheckImportStatus">
      <ImportStatusCheckerInternal {...props} />
    </Activity>
  </Scenario>
);
